import cn from 'classnames';
import { Tag } from 'sonata-react-components';

import { ChildItem } from './ChildItem';

import './mainItem.styles.scss';

import { isChildGroup, type SidebarMainItemConfig } from './sidebar.types';

type Props<T> = SidebarMainItemConfig<T> & {
  isCollapsed: boolean;
  isSelected: boolean;
};

export function MainItem<T>({
  checked,
  children,
  enabled,
  icon,
  id,
  isCollapsed,
  isSelected,
  onSectionClick,
  text
}: Readonly<Props<T>>) {
  const handleSectionClick = () => {
    onSectionClick(id);
  };

  const sectionErrorCount = (children ?? [])
    .flatMap(child => (isChildGroup(child) ? child.items : [child]))
    .reduce((total, item) => total + (item.errorCount ?? 0), 0);

  const mainItemHeaderClass = cn('mainItem__header', {
    'mainItem__header--collapsed': isCollapsed,
    'mainItem__header--selected': isSelected
  });

  const mainItemHeaderIconClass = cn('mainItem__headerIcon', {
    'mainItem__headerIcon--checked': checked,
    'mainItem__headerIcon--selected': isSelected
  });

  const mainItemHeaderTextClass = cn('mainItem__headerText', {
    'mainItem__headerText--checked': checked,
    'mainItem__headerText--selected': isSelected
  });

  const hasChildren = Array.isArray(children) && children.length > 0;
  const mainItemItemsContainerClass = cn('mainItem__items', {
    'mainItem__items--noChildren': isSelected && !hasChildren,
    'mainItem__items--opened': isSelected && !isCollapsed
  });

  return (
    <div className="mainItem">
      <div className="mainItem__container">
        {!enabled ? <div className="mainItem__veil" /> : null}
        <div className={mainItemHeaderClass} onClick={handleSectionClick}>
          <div className={mainItemHeaderIconClass}>
            <span className={cn('icon', icon)} />
          </div>
          {!isCollapsed && (
            <div className={mainItemHeaderTextClass}>
              <span>{text}</span>
              {sectionErrorCount > 0 && (
                <Tag className="mainItem__headerBadge" id={`main-item-error-badge-${id}`} rounded status="wrong" tiny>
                  {sectionErrorCount}
                </Tag>
              )}
            </div>
          )}
        </div>
        <div className={mainItemItemsContainerClass}>
          <div className="mainItem__itemsInner">
            {enabled &&
              isSelected &&
              hasChildren &&
              children.map(item =>
                isChildGroup(item) ? (
                  <div className="mainItem__group" key={item.label}>
                    <span className="mainItem__groupLabel">{item.label}</span>
                    <div className="mainItem__groupItems">
                      {item.items.map(childItem => (
                        <ChildItem key={childItem.id} {...childItem} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <ChildItem key={item.id} {...item} />
                )
              )}
          </div>
        </div>
      </div>
    </div>
  );
}
